import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import {
  normaliseInstrumentGroups,
  type SandboxInstrumentGroup,
} from '../lib/sandboxInstruments.js';

/**
 * The sandbox test-instrument reference, shown beneath the pay button.
 *
 * Renders NOTHING unless the gateway hands back a usable table, which is the
 * whole environment check — Baraka Pay 404s it in Live. A passenger on a real
 * booking therefore never sees this, and no flag has to be kept in step with the
 * deployment to make that true.
 *
 * Every failure is the same as "not sandbox": a decorative panel must never be
 * able to break a payment page.
 */
export function SandboxInstruments() {
  const [groups, setGroups] = useState<SandboxInstrumentGroup[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const payload = await api.request<unknown>('/payments/sandbox-instruments', {
          auth: false,
        });
        if (!cancelled) setGroups(normaliseInstrumentGroups(payload));
      } catch {
        // 404 outside sandbox, or anything else. Same answer: show nothing.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (groups.length === 0) return null;

  const total = groups.reduce((n, group) => n + group.instruments.length, 0);

  return (
    <aside
      className="mt-6 rounded-xl border"
      style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
      aria-label="Sandbox test values"
    >
      {/*
        * COLLAPSED BY DEFAULT, and it has to be: eleven rails and nearly fifty
        * instruments is longer than the form it sits beside, and a reference
        * panel that dwarfs the thing it refers to has stopped being a reference.
        * <details> rather than React state — the browser already does this, with
        * the keyboard and screen-reader behaviour included.
        */}
      <details>
        <summary className="cursor-pointer list-none px-4 py-3">
          <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em]"
                style={{ color: 'var(--color-faint)' }}>
            Sandbox test values
          </span>
          <span className="mt-0.5 block text-xs" style={{ color: 'var(--color-muted)' }}>
            This gateway is in sandbox — no money moves. {total} test values across{' '}
            {groups.length} payment methods.
          </span>
        </summary>

        <div className="flex flex-col gap-3 border-t px-4 py-3"
             style={{ borderColor: 'var(--color-line)' }}>
          {groups.map((group) => (
            <div key={group.paymentType}>
              <p className="text-xs font-semibold">{group.paymentType.replace(/_/g, ' ').toUpperCase()}</p>
              <ul className="mt-1 flex flex-col gap-1">
                {group.instruments.map((instrument) => (
                  <li key={`${group.paymentType}-${instrument.suffix}`}
                      className="flex items-baseline gap-2 text-xs">
                    {/* The suffix is the thing to copy, so it leads and is monospaced. */}
                    <code
                      className="rounded px-1.5 py-0.5 tabular"
                      style={{ background: 'var(--color-primary-wash)', color: 'var(--color-text)' }}
                    >
                      …{instrument.suffix}
                    </code>
                    <span style={{ color: 'var(--color-muted)' }}>{instrument.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </details>
    </aside>
  );
}
